import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { type LegacySiteHistoryLabels } from '../../generics/LegacySitePrimitives.js'
import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { loggerId, type LoggerId } from './Logger.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.legacy_site_logger_history_ */
export type LegacySiteLoggerHistoryId = string & {
  __brand: 'LegacySiteLoggerHistoryId'
}

/** Represents the table public.legacy_site_logger_history_ */
export default interface LegacySiteLoggerHistoryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    LegacySiteLoggerHistoryId,
    LegacySiteLoggerHistoryId | undefined,
    LegacySiteLoggerHistoryId
  >

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.int4 */
  loggerId: ColumnType<LoggerId | null, LoggerId | null, LoggerId | null>

  /** Database type: pg_catalog.timestamptz */
  startedAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.timestamptz */
  endedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.int4[] */
  decode: ColumnType<number[] | null, number[] | null, number[] | null>

  /** Database type: pg_catalog.text */
  imei: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  dataType: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.jsonb */
  labels: ColumnType<
    LegacySiteHistoryLabels | null,
    LegacySiteHistoryLabels | null,
    LegacySiteHistoryLabels | null
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>
}

export type LegacySiteLoggerHistory = Selectable<LegacySiteLoggerHistoryTable>

export type LegacySiteLoggerHistoryInitializer =
  Insertable<LegacySiteLoggerHistoryTable>

export type LegacySiteLoggerHistoryMutator =
  Updateable<LegacySiteLoggerHistoryTable>

export const legacySiteLoggerHistoryId =
  z.string() as unknown as z.Schema<LegacySiteLoggerHistoryId>

export const legacySiteLoggerHistory = z.object({
  id: legacySiteLoggerHistoryId,
  siteId: legacySiteId,
  loggerId: loggerId.nullable().optional(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().nullable().optional(),
  decode: z.number().array().nullable().optional(),
  imei: z.string().nullable().optional(),
  dataType: z.string().nullable().optional(),
  labels: z.unknown().nullable().optional(),
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
}) as unknown as z.Schema<LegacySiteLoggerHistory>

export const legacySiteLoggerHistoryInitializer = z.object({
  id: legacySiteLoggerHistoryId.optional(),
  siteId: legacySiteId,
  loggerId: loggerId.optional().nullable(),
  startedAt: z.coerce.date(),
  endedAt: z.coerce.date().optional().nullable(),
  decode: z.number().array().optional().nullable(),
  imei: z.string().optional().nullable(),
  dataType: z.string().optional().nullable(),
  labels: z.unknown().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteLoggerHistoryInitializer>

export const legacySiteLoggerHistoryMutator = z.object({
  id: legacySiteLoggerHistoryId.optional(),
  siteId: legacySiteId.optional(),
  loggerId: loggerId.optional().nullable(),
  startedAt: z.coerce.date().optional(),
  endedAt: z.coerce.date().optional().nullable(),
  decode: z.number().array().optional().nullable(),
  imei: z.string().optional().nullable(),
  dataType: z.string().optional().nullable(),
  labels: z.unknown().optional().nullable(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteLoggerHistoryMutator>
